import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Paper, FormControl, InputAdornment, IconButton, Grid, Button, Alert, Typography } from "@mui/material";

import Visibility from "assets/icon/ic_eye.svg";
import VisibilityOff from "assets/icon/ic_eye_off.png";

import LockIcon from "assets/icon/ic_key.png";

import Logo from "assets/logo/ic_bg_small@2x.png";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import Modal from "components/Dialog";
import Loading from "components/Loading";
import PasswordStrengthIndicator from "components/PasswordStrengthIndicator";

import { authService } from "services/authentication/auth.service";
import { setUser } from "store/reducer/appReducer";
import { RootState } from "store/store";

import FormInput from "components/FormInput";

import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { object, string } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Link, useSearchParams } from "react-router-dom";
import { IModal } from "interfaces/utils/utils.interface";
import { styles } from "assets/styles";
import { defaultModal } from "helpers/utility";
import { validatePasswordPolicy } from "helpers/passwordPolicy";
import api from "plugins/axios";

interface State {
  currentPassword: string;
  showCurrentPassword: boolean;
  password: string;
  showPassword: boolean;
  confirmPassword: string;
  showConfirmPassword: boolean;
}

// Dynamic schema based on force change mode
const createChangePasswordSchema = (isForceChange: boolean, policyConfig: { minPasswordLength: number; requireSpecialChar: boolean }) => {
  return object({
    currentPassword: isForceChange 
      ? string().optional() // Optional for force change
      : string().min(1, "กรุณาระบุรหัสผ่านปัจจุบัน"), // Required for normal change
    password: string()
      .min(1, "กรุณาระบุรหัสผ่าน")
      .refine((password) => {
        if (password.length === 0) return true; // Don't validate empty password (let .min() handle it)
        const result = validatePasswordPolicy(password, {
          minLength: policyConfig.minPasswordLength,
          requireSpecialChar: policyConfig.requireSpecialChar,
        });
        return result.isValid;
      }, " "),
    confirmPassword: string()
      .min(1, "กรุณาระบุรหัสผ่าน"),
  }).refine((data) => data.password === data.confirmPassword, {
    message: "รหัสผ่านไม่ตรงกัน",
    path: ["confirmPassword"],
  });
};

type IChangePassword = {
  currentPassword?: string;
  password: string;
  confirmPassword: string;
};

export default function ChangePassword() {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.user);
  
  const [searchParams] = useSearchParams();
  const [token] = React.useState<any>(searchParams.get("token"));
  const [passwordChangeToken] = React.useState<string | null>(
    localStorage.getItem("cmt_password_change_token")
  );
  const [tokenExpiry] = React.useState<number | null>(
    localStorage.getItem("cmt_password_change_token_expiry") 
      ? parseInt(localStorage.getItem("cmt_password_change_token_expiry")!)
      : null
  );

  const [state, setState] = useState<State>({
    currentPassword: "",
    showCurrentPassword: false,
    password: "",
    showPassword: false,
    confirmPassword: "",
    showConfirmPassword: false,
  });
  const [modal, setModal] = useState<IModal>(defaultModal);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [passwordPolicyConfig, setPasswordPolicyConfig] = useState<{
    minPasswordLength: number;
    requireSpecialChar: boolean;
  }>({
    minPasswordLength: 12, // default fallback
    requireSpecialChar: true,
  });
  const [policyConfigLoaded, setPolicyConfigLoaded] = useState<boolean>(false);

  // Check if token is expired
  const isTokenExpired = tokenExpiry && Date.now() > tokenExpiry;
  const isForceChange = passwordChangeToken && !isTokenExpired;
  const isForgotPasswordReset = !!token; // Reset from forgot password email

  useEffect(() => {
    // If token is expired, redirect to login
    if (passwordChangeToken && isTokenExpired) {
      localStorage.removeItem("cmt_password_change_token");
      localStorage.removeItem("cmt_password_change_token_expiry");
      setModal({
        open: true,
        message: "Token หมดอายุแล้ว กรุณาเข้าสู่ระบบใหม่",
        status: "error",
        navigateTo: "/login",
      });
    }
  }, [passwordChangeToken, isTokenExpired]);

  // Fetch password policy config
  useEffect(() => {
    const fetchPasswordPolicyConfig = async () => {
      try {
        const response = await api.get('/pub/password-policy-config');
        if (response.data && response.data.data) {
          setPasswordPolicyConfig({
            minPasswordLength: response.data.data.minPasswordLength || 12,
            requireSpecialChar: response.data.data.requireSpecialChar || true,
          });
          setPolicyConfigLoaded(true);
        }
      } catch (error) {
        // Use default values if fetch fails
        setPolicyConfigLoaded(true);
      }
    };

    fetchPasswordPolicyConfig();
  }, []);

  const defaultValues: IChangePassword = {
    currentPassword: "",
    password: "",
    confirmPassword: "",
  };
  
  // Create dynamic schema based on force change mode OR forgot password reset
  const changePasswordSchema = useMemo(() => 
    createChangePasswordSchema(isForceChange || isForgotPasswordReset, passwordPolicyConfig),
    [isForceChange, isForgotPasswordReset, passwordPolicyConfig]
  );
  
  const methods = useForm<IChangePassword>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onChange', // Validate on change to clear errors when typing
    defaultValues,
  });

  // Re-validate form when schema changes (especially when policyConfig loads)
  useEffect(() => {
    if (policyConfigLoaded) {
      methods.trigger(); // Re-validate all fields with new schema
    }
  }, [changePasswordSchema, policyConfigLoaded, methods]);

  const { watch } = methods;
  const passwordValue = watch("password");

  useEffect(() => {
    if (passwordValue) {
      setCurrentPassword(passwordValue);
    }
  }, [passwordValue]);

  const handleClickShowCurrentPassword = () => {
    setState({
      ...state,
      showCurrentPassword: !state.showCurrentPassword,
    });
  };
  const handleClickShowPassword = () => {
    setState({
      ...state,
      showPassword: !state.showPassword,
    });
  };
  const handleClickShowPasswordCF = () => {
    setState({
      ...state,
      showConfirmPassword: !state.showConfirmPassword,
    });
  };
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const onSubmitHandler: SubmitHandler<IChangePassword> = async (values: IChangePassword) => {
    setLoading(true);

    // Check if password is provided
    if (!values.password) {
      setModal({
        open: true,
        message: "กรุณาระบุรหัสผ่าน",
        status: "error",
      });
      setLoading(false);
      return;
    }

    // Double-check password policy on submission
    const policyResult = validatePasswordPolicy(values.password, {
      minLength: passwordPolicyConfig.minPasswordLength,
      requireSpecialChar: passwordPolicyConfig.requireSpecialChar,
    });
    
    if (!policyResult.isValid) {
      setModal({
        open: true,
        message: `รหัสผ่านไม่ตรงตามข้อกำหนด: ${policyResult.errors.join(", ")}`,
        status: "error",
      });
      setLoading(false);
      return;
    }

    // Check if this is a forced password change (with token) or reset password (with token from URL)
    if (isForceChange && passwordChangeToken) {
      // Force password change - use token instead of current password
      await authService
        .forceUpdatePassword(passwordChangeToken, values.password)
        .then(async (response) => {
          // Clear token from localStorage after successful change
          localStorage.removeItem("cmt_password_change_token");
          localStorage.removeItem("cmt_password_change_token_expiry");
          
          // Remove password change token and flag from localStorage
          localStorage.removeItem("cmt_password_change_token");
          localStorage.removeItem("cmt_password_change_token_expiry");
          localStorage.removeItem("cmt_require_password_change");
          
          // Update Redux state with updated admin data from backend
          if (response.data && response.data.data) {
            dispatch(setUser(response.data.data));
          } else if (user) {
            // Fallback: manually update user state
            const updatedUser = { ...user, requirePasswordChange: false };
            dispatch(setUser(updatedUser));
          }
          
          setLoading(false);
          setModal({
            open: true,
            message: "เปลี่ยนรหัสผ่านสำเร็จ ระบบได้บันทึกข้อมูลการเปลี่ยนแปลงแล้ว",
            status: "success",
            navigateTo: "/",
          });
        })
        .catch((err) => {
          setLoading(false);
          
          const errorMessage = err.response?.data?.message || "";
          
          if (errorMessage.includes("cannot be the same as current password") || 
              errorMessage.includes("same as current") ||
              errorMessage.includes("password validation failed")) {
            setModal({
              open: true,
              message: "รหัสผ่านใหม่ต้องไม่เหมือนกับรหัสผ่านปัจจุบัน",
              status: "error",
            });
          } else if (errorMessage.includes("password policy") || 
                     errorMessage.includes("policy")) {
            setModal({
              open: true,
              message: "รหัสผ่านไม่ตรงตามข้อกำหนดความปลอดภัย",
              status: "error",
            });
          } else {
            const displayMessage = errorMessage || "เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน";
            setModal({
              open: true,
              message: displayMessage,
              status: "error",
            });
          }
        });
    } else if (token) {
      // Reset password from forgot password email
      await authService
        .changePassword(values.password, token)
        .then(async () => {
          setLoading(false);
          setModal({
            open: true,
            message: "เปลี่ยนรหัสผ่านสำเร็จ",
            status: "success",
            navigateTo: "/login",
          });
        })
        .catch((err) => {
          setLoading(false);
          if (err.response?.status === 400) {
            if (err.response?.data?.errors?.includes("password policy")) {
              setModal({
                open: true,
                message: "รหัสผ่านไม่ตรงตามข้อกำหนดความปลอดภัย",
                status: "error",
              });
            } else {
              setModal({
                open: true,
                message: "เปลี่ยนรหัสผ่านไม่สำเร็จ",
                status: "error",
              });
            }
          } else {
            setModal({
              open: true,
              message: "เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง",
              status: "error",
            });
          }
        });
    } else {
      // Regular password change - need current password
      await authService
        .updateAdminPassword(values.currentPassword!, values.password)
        .then(async () => {
          setLoading(false);
          setModal({
            open: true,
            message: "เปลี่ยนรหัสผ่านสำเร็จ ระบบได้บันทึกข้อมูลการเปลี่ยนแปลงแล้ว",
            status: "success",
            navigateTo: "/login",
          });
        })
        .catch((err) => {
          setLoading(false);
          if (err.response?.status === 400) {
            if (err.response?.data?.message?.includes("current password")) {
              setModal({
                open: true,
                message: "รหัสผ่านปัจจุบันไม่ถูกต้อง",
                status: "error",
              });
            } else if (err.response?.data?.message?.includes("cannot be the same as current password")) {
              setModal({
                open: true,
                message: "รหัสผ่านใหม่ต้องไม่เหมือนกับรหัสผ่านปัจจุบัน",
                status: "error",
              });
            } else if (err.response?.data?.errors?.includes("password policy")) {
              setModal({
                open: true,
                message: "รหัสผ่านไม่ตรงตามข้อกำหนดความปลอดภัย",
                status: "error",
              });
            } else {
              setModal({
                open: true,
                message: err.response?.data?.message || "เกิดข้อผิดพลาด",
                status: "error",
              });
            }
          } else {
            setModal({
              open: true,
              message: err.response?.data?.message || "เกิดข้อผิดพลาด",
              status: "error",
            });
          }
        });
    }
  };

  return (
    <>
      <Loading loading={loading} />
      <Modal
        props={{
          modalOpen: modal.open,
          modalStatus: modal.status,
          modalMessage: modal.message,
          handleModal: setModal,
          navigateTo: modal.navigateTo,
        }}
      />
      <FormProvider {...methods}>
        <Grid
          style={styles.paperContainer}
          container
          sx={{
            height: "100%",
          }}
        >
          <Grid
            item
            xs={5}
            sm={12}
            md={5}
            lg={6}
            sx={{
              height: "100vh",
              display: { xs: "none", sm: "none", md: "block" },
            }}
          >
            <Grid
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              sx={{
                height: "100vh",
              }}
            >
              <img style={{ paddingLeft: "2px", paddingBottom: "5px" }} src={Logo} alt="logo" />
              <div style={styles.txtLeftPadding}>Central Metals (Thailand) Ltd.</div>
            </Grid>
          </Grid>
          <Grid container alignContent={"center"} justifyContent={"center"} item xs={12} sm={12} md={7} lg={6} sx={{ pr: 7, pl: 1, height: "100vh", overflow: "auto" }}>
            <Grid item xs={10} md={10}>
              <Paper elevation={4} square sx={{ height: "80vh", minHeight: "650px", maxHeight: "90vh" }}>
                <form onSubmit={methods.handleSubmit(
                  onSubmitHandler,
                  (errors) => {
                    // Handle validation errors if needed
                  }
                )}>
                  <Grid
                    container
                    sx={{
                      height: "100%",
                      py: 2,
                      overflow: "auto",
                    }}
                    justifyContent="center"
                  >
                    <Grid item xs={8}>
                      <Grid container item sx={{ pb: 3, pt: 2 }}>
                        <div
                          style={{
                            fontSize: "32px",
                          }}
                        >
                          <Link to="/">
                            <Button 
                              size="small" 
                              variant="contained" 
                              className="nav-back-btn" 
                              sx={styles.btnBack}
                              aria-label="กลับไปหน้าหลัก"
                              disabled={!!isForceChange} // Disable back button for force change
                            >
                              <ArrowBackIcon color="primary" sx={{ fontSize: 22, fontWeight: "bold" }}></ArrowBackIcon>
                            </Button>
                          </Link>
                          <label>{isForceChange ? "เปลี่ยนรหัสผ่าน (บังคับ)" : "เปลี่ยนรหัสผ่าน"}</label>
                        </div>
                      </Grid>
                      
                      {/* Security Policy Alert for Force Password Change */}
                      {isForceChange && policyConfigLoaded && (
                        <Grid item pb={1}>
                          <Alert severity="warning" sx={{ mb: 1 }}>
                            <Typography variant="body2">
                              <strong>คุณต้องเปลี่ยนรหัสผ่านเพื่อให้เป็นไปตามนโยบายความปลอดภัยใหม่</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              • อย่างน้อย {passwordPolicyConfig.minPasswordLength} ตัวอักษร<br/>
                              {passwordPolicyConfig.requireSpecialChar && (
                                <>• มีอักขระพิเศษอย่างน้อย 1 ตัว<br/></>
                              )}
                              • ต้องไม่เหมือนกับรหัสผ่านเดิม
                            </Typography>
                          </Alert>
                        </Grid>
                      )}
                      
                      {/* Current Password Field - Only show for regular password change (not force change) */}
                      {!isForceChange && !token && (
                        <Grid item pb={1}>
                          <FormControl fullWidth variant="outlined">
                            <b>รหัสผ่านปัจจุบัน</b>
                            <FormInput
                              variant="standard"
                              name="currentPassword"
                              placeholder="รหัสผ่านปัจจุบัน"
                              type={state.showCurrentPassword ? "text" : "password"}
                              inputProps={{
                                autoComplete: "current-password",
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <img style={{ paddingLeft: "2px" }} src={LockIcon} alt="iconLock" width="70%" />
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="toggle password visibility"
                                      onClick={handleClickShowCurrentPassword}
                                      onMouseDown={handleMouseDownPassword}
                                      edge="end"
                                    >
                                      {state.showCurrentPassword ? <img src={VisibilityOff} alt="iconVisibilityOff" width="60%" /> : <img src={Visibility} alt="iconVisibility" width="60%" />}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </FormControl>
                        </Grid>
                      )}
                      
                      {/* New Password Field */}
                      <Grid item pb={1}>
                        <FormControl fullWidth variant="outlined">
                          <b>รหัสผ่านใหม่</b>
                          <FormInput
                            variant="standard"
                            name="password"
                            placeholder="รหัสผ่านใหม่"
                            type={state.showPassword ? "text" : "password"}
                            inputProps={{
                              autoComplete: "new-password",
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <img style={{ paddingLeft: "2px" }} src={LockIcon} alt="iconLock" width="70%" />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                  >
                                    {state.showPassword ? <img src={VisibilityOff} alt="iconVisibilityOff" width="60%" /> : <img src={Visibility} alt="iconVisibility" width="60%" />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </FormControl>
                      </Grid>
                      
                      {/* Password Strength Indicator */}
                      <Grid item pb={1}>
                        {policyConfigLoaded && (
                          <PasswordStrengthIndicator 
                            password={currentPassword} 
                            compact={true} 
                            minPasswordLength={passwordPolicyConfig.minPasswordLength}
                            requireSpecialChar={passwordPolicyConfig.requireSpecialChar}
                          />
                        )}
                      </Grid>
                      
                      {/* Confirm Password Field */}
                      <Grid item pb={1}>
                        <FormControl fullWidth variant="outlined">
                          <b>ยืนยันรหัสผ่านใหม่</b>
                          <FormInput
                            variant="standard"
                            name="confirmPassword"
                            placeholder="ยืนยันรหัสผ่านใหม่"
                            type={state.showConfirmPassword ? "text" : "password"}
                            inputProps={{
                              autoComplete: "new-password",
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <img style={{ paddingLeft: "2px" }} src={LockIcon} alt="iconLock" width="70%" />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPasswordCF}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                  >
                                    {state.showConfirmPassword ? <img src={VisibilityOff} alt="iconVisibilityOff" width="60%" /> : <img src={Visibility} alt="iconVisibility" width="60%" />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </FormControl>
                      </Grid>
                      
                      {/* Submit Button */}
                      <Grid item pb={1}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          aria-label={isForceChange ? "เปลี่ยนรหัสผ่าน (บังคับ)" : "เปลี่ยนรหัสผ่าน"}
                          sx={{
                            mt: 2,
                            borderRadius: 3,
                          }}
                        >
                          {isForceChange ? "เปลี่ยนรหัสผ่าน (บังคับ)" : "เปลี่ยนรหัสผ่าน"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </FormProvider>
    </>
  );
}
